import React, { useState, useEffect } from 'react';
import { ChefHat, Star, Users, Calendar, Heart, ArrowRight, Award, Clock } from 'lucide-react';
import '../styles/home.css';
import { mockData } from '../mock';
import { formHandlers, trackPageView } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { LoyaltyWidget } from '../components/LoyaltyWidget';

export const Home = () => {
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  // Track page view on component mount
  useEffect(() => {
    trackPageView('/');
  }, []);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) return;

    setIsSubmitting(true);
    try {
      const result = await formHandlers.subscribeToNewsletter(newsletterEmail);
      if (result.success) {
        toast({
          title: "Success!",
          description: result.message,
        });
        setNewsletterEmail('');
      } else {
        toast({
          title: "Error",
          description: result.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {/* Full-Screen Hero Section - Inspired by Luxury Design */}
      <section style={{ 
        position: 'relative',
        height: '100vh',
        minHeight: '800px',
        background: `
          linear-gradient(
            rgba(0, 0, 0, 0.4), 
            rgba(0, 0, 0, 0.6)
          ),
          url('https://customer-assets.emergentagent.com/job_72634f19-da8e-4ecb-82d2-0080f098116d/artifacts/xrenh678_IMG_0447.jpeg') center/cover no-repeat
        `,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
      }}>
        {/* Content Overlay */}
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div style={{ 
            textAlign: 'center',
            maxWidth: '900px',
            margin: '0 auto',
            padding: '0 20px'
          }}>
            {/* Elegant Logo Display */}
            <div style={{ marginBottom: '40px', opacity: 0.9 }}>
              <img
                src="https://customer-assets.emergentagent.com/job_chef-luxury-1/artifacts/4gytiwjf_IMG_0369.jpeg"
                alt="Decadent Palete"
                style={{
                  height: '120px',
                  width: 'auto',
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 4px 20px rgba(212, 175, 55, 0.4))'
                }}
              />
            </div>
            
            {/* Main Headlines - Luxury Atelier Positioning */}
            <h1 style={{ 
              fontSize: 'clamp(3rem, 6vw, 5.5rem)',
              fontWeight: '300',
              color: '#ffffff',
              marginBottom: '16px',
              letterSpacing: '0.02em',
              lineHeight: '1.1',
              fontFamily: "'Playfair Display', serif"
            }}>
              Decadent Palete
            </h1>
            
            <p style={{ 
              fontSize: 'clamp(1.1rem, 2vw, 1.4rem)',
              color: 'rgba(255, 255, 255, 0.9)',
              marginBottom: '48px',
              letterSpacing: '0.05em',
              fontWeight: '400',
              maxWidth: '900px',
              margin: '0 auto 48px auto',
              lineHeight: '1.5'
            }}>
              We provide high-end luxury hospitality experiences from large to small events, weddings, travel groups, retreats, and elite celebrations reaching unparalleled sophistication. We don't just cook — we provide unforgettable moments and services.
            </p>
            
            {/* Unique CTA Button - Inspired by reference */}
            <div style={{ marginBottom: '80px' }}>
              <Button 
                className="btn-gold"
                style={{
                  fontSize: '16px',
                  fontWeight: '500',
                  padding: '18px 36px',
                  borderRadius: '50px',
                  background: 'linear-gradient(135deg, #d4af37 0%, #b8941f 100%)',
                  border: 'none',
                  color: '#000000',
                  textTransform: 'none',
                  letterSpacing: '0.5px',
                  boxShadow: '0 8px 24px rgba(212, 175, 55, 0.3)',
                  transition: 'all 0.3s ease',
                  minWidth: '220px',
                  height: 'auto'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 12px 32px rgba(212, 175, 55, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 8px 24px rgba(212, 175, 55, 0.3)';
                }}
              >
                Reserve Your Experience
                <ArrowRight size={18} style={{ marginLeft: '8px' }} />
              </Button>
            </div>
            
            {/* Subtle scroll indicator - inspired by reference */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              color: 'rgba(255, 255, 255, 0.6)',
              animation: 'gentle-pulse 2s ease-in-out infinite'
            }}>
              <div style={{
                width: '24px',
                height: '1px',
                background: 'rgba(255, 255, 255, 0.4)',
                marginBottom: '8px'
              }}></div>
              <div style={{
                width: '2px',
                height: '24px',
                background: 'rgba(255, 255, 255, 0.4)'
              }}></div>
              <div style={{
                width: '24px',
                height: '1px',
                background: 'rgba(255, 255, 255, 0.4)',
                marginTop: '8px'
              }}></div>
            </div>
          </div>
        </div>
        
        {/* Subtle overlay pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at center, transparent 0%, rgba(0,0,0,0.2) 100%)',
          zIndex: 1
        }}></div>
      </section>

      {/* Sophisticated Services Showcase */}
      <section style={{ 
        background: '#000000',
        padding: '120px 0'
      }}>
        <div className="container">
          {/* Section Header */}
          <div style={{ 
            textAlign: 'center', 
            marginBottom: '80px',
            maxWidth: '800px',
            margin: '0 auto 80px auto'
          }}>
            <h2 style={{ 
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              fontWeight: '300',
              color: '#ffffff',
              marginBottom: '24px',
              letterSpacing: '0.02em',
              fontFamily: "'Playfair Display', serif"
            }}>
              Atelier/Concierge Hospitality Services
            </h2>
            <p style={{ 
              fontSize: '1.1rem',
              color: 'rgba(255, 255, 255, 0.7)',
              lineHeight: '1.6',
              letterSpacing: '0.02em'
            }}>
              White-glove hospitality management for ultra-high-net-worth individuals and discerning clientele who demand nothing less than perfection
            </p>
          </div>

          {/* Three Pillars of Excellence */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '60px',
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            {/* Pillar 1 */}
            <div style={{ 
              textAlign: 'center',
              padding: '40px 20px'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                background: 'linear-gradient(135deg, #d4af37 0%, #b8941f 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 32px auto',
                boxShadow: '0 8px 24px rgba(212, 175, 55, 0.2)'
              }}>
                <ChefHat size={36} color="#000000" />
              </div>
              <h3 style={{ 
                fontSize: '1.3rem',
                fontWeight: '500',
                color: '#ffffff',
                marginBottom: '16px',
                letterSpacing: '0.02em'
              }}>
                Events & Weddings
              </h3>
              <p style={{ 
                color: 'rgba(255, 255, 255, 0.7)',
                lineHeight: '1.6',
                fontSize: '1rem'
              }}>
                From intimate gatherings to grand celebrations, we create luxury hospitality experiences for events of every scale. Our wedding and event services deliver sophistication and elegance that transforms your special moments into extraordinary memories.
              </p>
            </div>

            {/* Pillar 2 */}
            <div style={{ 
              textAlign: 'center',
              padding: '40px 20px'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                background: 'linear-gradient(135deg, #d4af37 0%, #b8941f 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 32px auto',
                boxShadow: '0 8px 24px rgba(212, 175, 55, 0.2)'
              }}>
                <Award size={36} color="#000000" />
              </div>
              <h3 style={{ 
                fontSize: '1.3rem',
                fontWeight: '500',
                color: '#ffffff',
                marginBottom: '16px',
                letterSpacing: '0.02em'
              }}>
                Travel Groups & Retreats
              </h3>
              <p style={{ 
                color: 'rgba(255, 255, 255, 0.7)',
                lineHeight: '1.6',
                fontSize: '1rem'
              }}>
                Curated luxury hospitality for exclusive travel groups and immersive retreats where sophistication meets adventure. We orchestrate transformative experiences that elevate your journeys to unparalleled heights of refinement.
              </p>
            </div>

            {/* Pillar 3 */}
            <div style={{ 
              textAlign: 'center',
              padding: '40px 20px'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                background: 'linear-gradient(135deg, #d4af37 0%, #b8941f 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 32px auto',
                boxShadow: '0 8px 24px rgba(212, 175, 55, 0.2)'
              }}>
                <Clock size={36} color="#000000" />
              </div>
              <h3 style={{ 
                fontSize: '1.3rem',
                fontWeight: '500',
                color: '#ffffff',
                marginBottom: '16px',
                letterSpacing: '0.02em'
              }}>
                Elite Celebrations
              </h3>
              <p style={{ 
                color: 'rgba(255, 255, 255, 0.7)',
                lineHeight: '1.6',
                fontSize: '1rem'
              }}>
                Sophisticated hospitality production for extraordinary celebrations that demand unparalleled sophistication. We craft milestone moments that transcend expectations and create lasting legacies.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Signature Experiences */}
      <section style={{ 
        background: '#fafafa',
        padding: '120px 0'
      }}>
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '80px',
            alignItems: 'center',
            maxWidth: '1400px',
            margin: '0 auto'
          }}>
            {/* Content Side */}
            <div style={{ padding: '0 20px' }}>
              <h2 style={{ 
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: '300',
                color: '#1a1a1a',
                marginBottom: '32px',
                letterSpacing: '0.02em',
                fontFamily: "'Playfair Display', serif",
                lineHeight: '1.2'
              }}>
                Where Passion Meets Precision
              </h2>
              
              <p style={{ 
                fontSize: '1.1rem',
                color: '#666666',
                lineHeight: '1.7',
                marginBottom: '32px',
                letterSpacing: '0.02em'
              }}>
                Every culinary journey begins with a story rooted in heritage. Our artisans honor Caribbean soul traditions, 
                elevating them with French sophistication and Indian spice mastery to create 
                experiences that nourish both body and spirit.
              </p>

              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap',
                gap: '16px', 
                marginBottom: '40px' 
              }}>
                <div style={{
                  padding: '8px 20px',
                  background: '#d4af37',
                  color: '#000000',
                  fontSize: '12px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '1.2px',
                  borderRadius: '2px'
                }}>
                  Caribbean Soul
                </div>
                <div style={{
                  padding: '8px 20px',
                  background: '#1a1a1a',
                  color: '#ffffff',
                  fontSize: '12px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '1.2px',
                  borderRadius: '2px'
                }}>
                  French Technique
                </div>
                <div style={{
                  padding: '8px 20px',
                  background: '#4a5568',
                  color: '#ffffff',
                  fontSize: '12px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '1.2px',
                  borderRadius: '2px'
                }}>
                  Indian Influence
                </div>
              </div>
              
              <Button 
                className="btn-primary"
                style={{
                  background: '#1a1a1a',
                  color: '#ffffff',
                  border: 'none',
                  padding: '16px 32px',
                  fontSize: '15px',
                  fontWeight: '500',
                  letterSpacing: '0.5px',
                  borderRadius: '2px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#333333';
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#1a1a1a';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                Discover Our Philosophy
                <ArrowRight size={16} style={{ marginLeft: '8px' }} />
              </Button>
            </div>
            
            {/* Image Side */}
            <div style={{ 
              position: 'relative',
              minHeight: '500px',
              display: 'flex',
              alignItems: 'center'
            }}>
              <div style={{
                position: 'relative',
                width: '100%',
                height: '500px',
                overflow: 'hidden',
                borderRadius: '4px',
                boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
              }}>
                <img
                  src={mockData.heroImages[0].url}
                  alt="Signature Culinary Artistry"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transition: 'transform 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'scale(1.02)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'scale(1)';
                  }}
                />
                <div style={{
                  position: 'absolute',
                  bottom: '0',
                  left: '0',
                  right: '0',
                  background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                  padding: '40px 32px 32px 32px',
                  color: 'white'
                }}>
                  <p style={{ 
                    margin: 0, 
                    color: '#d4af37',
                    fontSize: '14px',
                    fontWeight: '600',
                    letterSpacing: '1px',
                    textTransform: 'uppercase'
                  }}>
                    Artisanal Excellence
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Curated Gallery */}
      <section style={{ 
        background: '#000000',
        padding: '120px 0'
      }}>
        <div className="container">
          <h2 style={{ 
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            fontWeight: '300',
            color: '#ffffff',
            textAlign: 'center',
            marginBottom: '80px',
            letterSpacing: '0.02em',
            fontFamily: "'Playfair Display', serif"
          }}>
            Culinary Artistry
          </h2>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '32px'
          }}>
            {mockData.chefSignatureCreations.map((image, index) => (
              <div 
                key={image.id} 
                style={{ 
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: '8px',
                  height: index === 0 ? '400px' : '320px',
                  cursor: 'pointer',
                  background: '#1a1a1a'
                }}
                onMouseEnter={(e) => {
                  const img = e.currentTarget.querySelector('img');
                  const overlay = e.currentTarget.querySelector('.hover-overlay');
                  if (img) img.style.transform = 'scale(1.05)';
                  if (overlay) overlay.style.opacity = '1';
                }}
                onMouseLeave={(e) => {
                  const img = e.currentTarget.querySelector('img');
                  const overlay = e.currentTarget.querySelector('.hover-overlay');
                  if (img) img.style.transform = 'scale(1)';
                  if (overlay) overlay.style.opacity = '0';
                }}
              >
                <img
                  src={image.url}
                  alt={image.alt}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transition: 'transform 0.6s ease'
                  }}
                />
                {/* Hover Overlay */}
                <div 
                  className="hover-overlay"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.9), rgba(184, 148, 31, 0.9))',
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    textAlign: 'center',
                    padding: '20px'
                  }}
                >
                  <h3 style={{ 
                    color: '#000000', 
                    fontSize: '1.3rem',
                    fontWeight: '600',
                    marginBottom: '12px',
                    letterSpacing: '0.02em'
                  }}>
                    {image.title}
                  </h3>
                  <p style={{ 
                    color: '#000000', 
                    fontSize: '0.95rem',
                    opacity: 0.9,
                    lineHeight: '1.4'
                  }}>
                    {image.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Exclusive Experiences Preview */}
      <section style={{ 
        background: '#fafafa',
        padding: '120px 0'
      }}>
        <div className="container">
          <div style={{ 
            textAlign: 'center', 
            marginBottom: '80px',
            maxWidth: '800px',
            margin: '0 auto 80px auto'
          }}>
            <h2 style={{ 
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              fontWeight: '300',
              color: '#1a1a1a',
              marginBottom: '24px',
              letterSpacing: '0.02em',
              fontFamily: "'Playfair Display', serif"
            }}>
              Signature Experiences
            </h2>
            <p style={{ 
              fontSize: '1.1rem',
              color: '#666666',
              lineHeight: '1.6',
              letterSpacing: '0.02em'
            }}>
              Each moment is thoughtfully designed to create lasting memories
            </p>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '40px'
          }}>
            {mockData.eventImages.map((image, index) => (
              <div 
                key={image.id} 
                style={{ 
                  position: 'relative',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  height: '300px',
                  cursor: 'pointer',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 16px 32px rgba(0,0,0,0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)';
                }}
              >
                <img
                  src={image.url}
                  alt={image.alt}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
                <div style={{
                  position: 'absolute',
                  bottom: '0',
                  left: '0',
                  right: '0',
                  background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                  padding: '40px 24px 24px 24px',
                  color: 'white'
                }}>
                  <h3 style={{ 
                    fontSize: '1.2rem',
                    fontWeight: '500',
                    marginBottom: '8px',
                    letterSpacing: '0.02em'
                  }}>
                    {image.title}
                  </h3>
                  <p style={{ 
                    fontSize: '0.9rem',
                    opacity: 0.9,
                    margin: 0,
                    lineHeight: '1.4'
                  }}>
                    Thoughtfully curated for {index === 0 ? 'intimate gatherings' : index === 1 ? 'corporate excellence' : 'grand celebrations'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>



      {/* Features Section - Reimagined */}
      <section style={{ 
        background: '#1a1a1a',
        padding: '120px 0'
      }}>
        <div className="container">
          <div style={{ 
            textAlign: 'center', 
            marginBottom: '80px',
            maxWidth: '800px',
            margin: '0 auto 80px auto'
          }}>
            <h2 style={{ 
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              fontWeight: '300',
              color: '#ffffff',
              marginBottom: '24px',
              letterSpacing: '0.02em',
              fontFamily: "'Playfair Display', serif"
            }}>
              The Decadent Palete Difference
            </h2>
            <p style={{ 
              fontSize: '1.1rem',
              color: 'rgba(255, 255, 255, 0.7)',
              lineHeight: '1.6',
              letterSpacing: '0.02em'
            }}>
              Excellence is not just our standard—it's our promise
            </p>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '60px'
          }}>
            <div style={{ 
              textAlign: 'center', 
              padding: '20px'
            }}>
              <div style={{
                width: '100px',
                height: '100px',
                background: 'linear-gradient(135deg, #d4af37 0%, #b8941f 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 32px auto',
                boxShadow: '0 12px 28px rgba(212, 175, 55, 0.25)'
              }}>
                <ChefHat size={42} color="#000000" />
              </div>
              <h3 style={{ 
                fontSize: '1.4rem',
                fontWeight: '500',
                color: '#ffffff',
                marginBottom: '20px',
                letterSpacing: '0.02em'
              }}>
                Culinary Mastery
              </h3>
              <p style={{ 
                color: 'rgba(255, 255, 255, 0.75)',
                lineHeight: '1.7',
                fontSize: '1rem',
                letterSpacing: '0.02em'
              }}>
                Our culinary team combines decades of international experience with an unwavering commitment to innovation and excellence
              </p>
            </div>
            
            <div style={{ 
              textAlign: 'center', 
              padding: '20px'
            }}>
              <div style={{
                width: '100px',
                height: '100px',
                background: 'linear-gradient(135deg, #d4af37 0%, #b8941f 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 32px auto',
                boxShadow: '0 12px 28px rgba(212, 175, 55, 0.25)'
              }}>
                <Star size={42} color="#000000" />
              </div>
              <h3 style={{ 
                fontSize: '1.4rem',
                fontWeight: '500',
                color: '#ffffff',
                marginBottom: '20px',
                letterSpacing: '0.02em'
              }}>
                Uncompromising Quality
              </h3>
              <p style={{ 
                color: 'rgba(255, 255, 255, 0.75)',
                lineHeight: '1.7',
                fontSize: '1rem',
                letterSpacing: '0.02em'
              }}>
                Every ingredient is meticulously sourced, every technique refined, ensuring each dining experience exceeds expectations
              </p>
            </div>
            
            <div style={{ 
              textAlign: 'center', 
              padding: '20px'
            }}>
              <div style={{
                width: '100px',
                height: '100px',
                background: 'linear-gradient(135deg, #d4af37 0%, #b8941f 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 32px auto',
                boxShadow: '0 12px 28px rgba(212, 175, 55, 0.25)'
              }}>
                <Heart size={42} color="#000000" />
              </div>
              <h3 style={{ 
                fontSize: '1.4rem',
                fontWeight: '500',
                color: '#ffffff',
                marginBottom: '20px',
                letterSpacing: '0.02em'
              }}>
                Personalized Care
              </h3>
              <p style={{ 
                color: 'rgba(255, 255, 255, 0.75)',
                lineHeight: '1.7',
                fontSize: '1rem',
                letterSpacing: '0.02em'
              }}>
                Your vision becomes our mission. Every detail is thoughtfully considered to create truly memorable experiences
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Indian Fusion Menu Showcase */}
      <section style={{ 
        background: '#000000',
        padding: '120px 0'
      }}>
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
            gap: '80px',
            alignItems: 'center',
            maxWidth: '1400px',
            margin: '0 auto'
          }}>
            {/* Content Side */}
            <div style={{ padding: '0 20px' }}>
              <h2 style={{ 
                fontSize: 'clamp(2.5rem, 4vw, 3.5rem)',
                fontWeight: '300',
                color: '#ffffff',
                marginBottom: '32px',
                letterSpacing: '0.02em',
                fontFamily: "'Playfair Display', serif",
                lineHeight: '1.2'
              }}>
                Discover Our<br />Indian Fusion Collection
              </h2>
              
              <p style={{ 
                fontSize: '1.1rem',
                color: 'rgba(255, 255, 255, 0.8)',
                lineHeight: '1.7',
                marginBottom: '32px',
                letterSpacing: '0.02em'
              }}>
                Experience the harmonious marriage of Caribbean soul, French technique, and Indian aromatic spices. 
                Our signature fusion menu showcases innovative dishes that honor Caribbean heritage while embracing culinary creativity.
              </p>

              <div style={{ 
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '24px', 
                marginBottom: '40px' 
              }}>
                <div style={{
                  padding: '20px',
                  background: 'rgba(212, 175, 55, 0.1)',
                  borderRadius: '8px',
                  border: '1px solid rgba(212, 175, 55, 0.2)'
                }}>
                  <h3 style={{
                    color: '#d4af37',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    marginBottom: '8px'
                  }}>
                    Signature Appetizers
                  </h3>
                  <p style={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '0.9rem',
                    margin: 0
                  }}>
                    Coconut curry shrimp croquettes, tandoori spiced beef tartare
                  </p>
                </div>
                
                <div style={{
                  padding: '20px',
                  background: 'rgba(212, 175, 55, 0.1)',
                  borderRadius: '8px',
                  border: '1px solid rgba(212, 175, 55, 0.2)'
                }}>
                  <h3 style={{
                    color: '#d4af37',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    marginBottom: '8px'
                  }}>
                    Fusion Mains
                  </h3>
                  <p style={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '0.9rem',
                    margin: 0
                  }}>
                    Cardamom-braised lamb, curry-glazed duck, masala-spiced halibut
                  </p>
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <button
                  onClick={() => window.location.href = '/indian-fusion-menu'}
                  style={{
                    background: 'linear-gradient(135deg, #d4af37 0%, #b8941f 100%)',
                    color: '#000000',
                    border: 'none',
                    padding: '16px 32px',
                    fontSize: '16px',
                    fontWeight: '600',
                    letterSpacing: '0.5px',
                    borderRadius: '6px',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 8px 24px rgba(212, 175, 55, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  Explore Full Menu
                </button>
                
                <button
                  onClick={() => window.location.href = '/inquiry'}
                  style={{
                    background: 'transparent',
                    color: '#ffffff',
                    border: '1px solid rgba(212, 175, 55, 0.5)',
                    padding: '16px 32px',
                    fontSize: '16px',
                    fontWeight: '500',
                    letterSpacing: '0.5px',
                    borderRadius: '6px',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(212, 175, 55, 0.1)';
                    e.target.style.borderColor = '#d4af37';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'transparent';
                    e.target.style.borderColor = 'rgba(212, 175, 55, 0.5)';
                  }}
                >
                  Book Experience
                </button>
              </div>
            </div>
            
            {/* Featured Dishes Grid */}
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '20px',
              minHeight: '500px'
            }}>
              {mockData.chefSignatureCreations.slice(2, 6).map((dish, index) => (
                <div
                  key={dish.id}
                  style={{
                    position: 'relative',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    background: '#1a1a1a',
                    height: index === 0 ? '240px' : '230px'
                  }}
                >
                  <img
                    src={dish.url}
                    alt={dish.alt}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'scale(1)';
                    }}
                  />
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                    padding: '20px 16px 16px 16px'
                  }}>
                    <h3 style={{
                      color: '#ffffff',
                      fontSize: '1rem',
                      fontWeight: '600',
                      margin: '0 0 4px 0'
                    }}>
                      {dish.title}
                    </h3>
                    <p style={{
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontSize: '0.85rem',
                      margin: 0,
                      lineHeight: '1.3'
                    }}>
                      {dish.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Loyalty Widget */}
      <section style={{ 
        background: '#fafafa',
        padding: '60px 0'
      }}>
        <div className="container">
          <LoyaltyWidget />
        </div>
      </section>

      {/* Newsletter Signup - Luxury Style */}
      <section style={{ 
        background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)',
        padding: '100px 0'
      }}>
        <div className="container">
          <div style={{ 
            textAlign: 'center', 
            maxWidth: '700px', 
            margin: '0 auto'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              background: 'linear-gradient(135deg, #d4af37 0%, #b8941f 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 32px auto',
              boxShadow: '0 12px 28px rgba(212, 175, 55, 0.3)'
            }}>
              <Calendar size={36} color="#000000" />
            </div>
            
            <h2 style={{ 
              fontSize: 'clamp(1.8rem, 3vw, 2.5rem)',
              fontWeight: '300',
              color: '#ffffff',
              marginBottom: '20px',
              letterSpacing: '0.02em',
              fontFamily: "'Playfair Display', serif"
            }}>
              Join Our Culinary Journey
            </h2>
            
            <p style={{ 
              fontSize: '1.1rem',
              color: 'rgba(255, 255, 255, 0.8)',
              marginBottom: '40px',
              lineHeight: '1.6',
              letterSpacing: '0.02em'
            }}>
              Be the first to discover exclusive recipes, insider culinary insights, and invitations to intimate dining experiences
            </p>
            
            <form 
              onSubmit={handleNewsletterSubmit} 
              style={{ 
                display: 'flex', 
                gap: '16px', 
                maxWidth: '500px', 
                margin: '0 auto',
                flexWrap: 'wrap',
                justifyContent: 'center'
              }}
            >
              <Input
                type="email"
                placeholder="Your email address"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                required
                style={{ 
                  borderRadius: '4px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  background: 'rgba(255, 255, 255, 0.05)',
                  color: '#ffffff',
                  padding: '16px 20px',
                  fontSize: '16px',
                  minWidth: '300px',
                  flexGrow: 1
                }}
              />
              <Button 
                type="submit" 
                disabled={isSubmitting}
                style={{
                  background: 'linear-gradient(135deg, #d4af37 0%, #b8941f 100%)',
                  border: 'none',
                  color: '#000000',
                  padding: '16px 32px',
                  fontSize: '15px',
                  fontWeight: '600',
                  letterSpacing: '0.5px',
                  borderRadius: '4px',
                  transition: 'all 0.3s ease',
                  minWidth: '140px',
                  boxShadow: '0 4px 12px rgba(212, 175, 55, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 6px 16px rgba(212, 175, 55, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(212, 175, 55, 0.3)';
                }}
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </Button>
            </form>
            
            <p style={{ 
              fontSize: '0.9rem',
              color: 'rgba(255, 255, 255, 0.5)',
              marginTop: '24px',
              letterSpacing: '0.02em'
            }}>
              We respect your privacy. Unsubscribe at any time.
            </p>
          </div>
        </div>
      </section>

    </div>
  );
};